import React from "react"
import PropTypes from "prop-types"
import { Box, Flex, Grid, Heading, Link, Text, chakra } from "@chakra-ui/react"
import { Link as GatsbyLink } from "gatsby"
import { FaLinkedinIn } from "react-icons/fa"
import { FaXTwitter } from "react-icons/fa6"
import MenuLink from "./navigation/menu-link"
import { sections, social, summit, login } from "./navigation/menu"
import logo from "../images/logos/footer-logo.svg"

const desktop = "@media (min-width: 1101px)"
const tablet = "@media (max-width: 1100px)"
const phone = "@media (max-width: 640px)"

const icons = { LinkedIn: FaLinkedinIn, Twitter: FaXTwitter }

// Tight line height inside a link and padding between links, so a label that
// wraps still reads as one item. Capping labels at 190px on desktop lets the six
// columns fit the 1111px container with only the longest labels wrapping.
const link = {
  display: "block",
  py: "5px",
  color: "#C9D1DE",
  fontSize: "14px",
  lineHeight: "20px",
  _hover: { color: "white", textDecoration: "none" },
  [desktop]: { maxWidth: "190px" },
}

const styles = {
  root: {
    bg: "#1B263F",
    color: "white",
    pt: "56px",
    pb: "40px",
  },
  // Same container as Porter's Bundled Payments sections, so their edges line up.
  wrap: {
    maxWidth: "1111px",
    mx: "auto",
    px: "24px",
  },
  // Columns size to their content with the spare room spread between them, and
  // headings stay on one line.
  grid: {
    gridTemplateColumns: "auto repeat(5, auto)",
    justifyContent: "space-between",
    columnGap: "20px",
    rowGap: "40px",
    [tablet]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
    [phone]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: "24px",
    },
  },
  "logo-link": {
    display: "inline-block",
    lineHeight: 0,
  },
  // max-width: none stops the grid from squeezing the logo when space is tight.
  logo: {
    display: "block",
    width: "132px",
    maxWidth: "none",
    height: "auto",
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "1px solid #3B4866",
    color: "#C9D1DE",
    fontSize: "15px",
    _hover: { color: "white", borderColor: "white" },
  },
  "menu-title": {
    mb: "9px",
    color: "white",
    fontSize: "15px",
    fontWeight: 700,
    lineHeight: "22px",
    [desktop]: { whiteSpace: "nowrap" },
  },
  "menu-link": link,
  note: {
    maxWidth: "900px",
    mt: "32px",
    color: "#8F9BB3",
    fontSize: "13.5px",
    lineHeight: "20px",
  },
  bottom: {
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px 32px",
    mt: "38px",
    pt: "20px",
    borderTop: "1px solid #2B3752",
  },
  copyright: {
    color: "#8F9BB3",
    fontSize: "13px",
  },
  actions: {
    alignItems: "center",
    gap: "24px",
  },
  icons: {
    gap: "12px",
  },
  "action-link": {
    color: "white",
    fontSize: "14px",
    fontWeight: 600,
    _hover: { color: "white", textDecoration: "underline" },
  },
}

const Footer = ({ note }) => (
  <Box as="footer" sx={styles.root}>
    <Box sx={styles.wrap}>
      <Grid sx={styles.grid}>
        <Box>
          <Link as={GatsbyLink} to="/" sx={styles["logo-link"]}>
            <chakra.img
              src={logo}
              alt="Avant-garde Health logo"
              sx={styles.logo}
            />
          </Link>
        </Box>
        {sections.map(section => (
          <Box key={section.title}>
            <Heading as="h4" sx={styles["menu-title"]}>
              {section.title}
            </Heading>
            {section.links.map(item => (
              <MenuLink
                key={item.label}
                link={item}
                label={item.footerLabel || item.label}
                sx={styles["menu-link"]}
              />
            ))}
          </Box>
        ))}
      </Grid>
      {note && <Text sx={styles.note}>{note}</Text>}
      <Flex sx={styles.bottom}>
        <Text sx={styles.copyright}>
          © Avant-garde Health · Improving quality and profitability across the
          care continuum
        </Text>
        <Flex sx={styles.actions}>
          <Flex sx={styles.icons}>
            {social.map(({ label, href }) => {
              const Icon = icons[label]
              return (
                <Link
                  key={label}
                  href={href}
                  isExternal
                  aria-label={label}
                  sx={styles.icon}
                >
                  <Icon />
                </Link>
              )
            })}
          </Flex>
          <MenuLink link={summit} sx={styles["action-link"]} />
          <MenuLink link={login} sx={styles["action-link"]} />
        </Flex>
      </Flex>
    </Box>
  </Box>
)

Footer.propTypes = {
  note: PropTypes.node,
}

export default Footer
